// Keeps the deck.gl arrow glyph in step with the DOM Polyline's, rasterised once and shared across surfaces.
/* eslint-disable no-continue */

// Approximate metres per pixel at the equator at a given zoom level (Web Mercator).
const M_PER_PX_AT_EQUATOR = 156543

// ---------------------------------------------------------------------------
// Arrow icon — module-level cache so every surface shares one canvas + texture
// ---------------------------------------------------------------------------

/* eslint-disable no-underscore-dangle -- module-private caches; intentional `_` prefix */
let _arrowAtlas = null
let _arrowMapping = null
/* eslint-enable no-underscore-dangle */

// Drawn white on transparent because `mask: true` tints it per-segment via `getColor`.
export function getArrowAtlas() {
  if (_arrowAtlas) return { iconAtlas: _arrowAtlas, iconMapping: _arrowMapping }

  const cssSize = 16
  const dpi = typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2
  const px = Math.ceil(cssSize * dpi)

  const canvas = document.createElement('canvas')
  canvas.width = px
  canvas.height = px
  const ctx = canvas.getContext('2d')
  ctx.scale(dpi, dpi)

  // Longer than it is wide, so the apex reads as a direction rather than a dot.
  const s = cssSize
  const halfWidth = s * 0.45
  ctx.fillStyle = 'white'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.moveTo(s * 0.5, 1) // tip (top centre)
  ctx.lineTo(s * 0.5 + halfWidth, s - 1.5) // bottom-right
  ctx.lineTo(s * 0.5 - halfWidth, s - 1.5) // bottom-left
  ctx.closePath()
  ctx.fill()

  _arrowAtlas = canvas
  _arrowMapping = {
    arrow: { x: 0, y: 0, width: px, height: px, anchorX: px / 2, anchorY: px / 2, mask: true },
  }
  return { iconAtlas: _arrowAtlas, iconMapping: _arrowMapping }
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/**
 * Geographic bearing from (lng1, lat1) → (lng2, lat2), in degrees [0, 360),
 * measured clockwise from north.
 */
export function computeBearing(lng1, lat1, lng2, lat2) {
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const lat1R = (lat1 * Math.PI) / 180
  const lat2R = (lat2 * Math.PI) / 180
  const y = Math.sin(dLng) * Math.cos(lat2R)
  const x = Math.cos(lat1R) * Math.sin(lat2R) - Math.sin(lat1R) * Math.cos(lat2R) * Math.cos(dLng)
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360
}

/**
 * Haversine distance in metres between two [lng, lat] points. Fast enough for
 * per-frame arrow sampling — no trig library needed.
 */
export function distanceMetres(p0, p1) {
  const dLat = ((p1[1] - p0[1]) * Math.PI) / 180
  const dLng = ((p1[0] - p0[0]) * Math.PI) / 180
  const lat0 = (p0[1] * Math.PI) / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat0) * Math.cos(lat0 + dLat) * Math.sin(dLng / 2) ** 2
  return 2 * 6371000 * Math.asin(Math.sqrt(a))
}

/**
 * Sample arrow decorators at a fixed geographic spacing along a polyline.
 *
 * Each result carries:
 *   position — [lng, lat] of the arrow centre
 *   angle    — degrees, negated bearing (deck.gl IconLayer's `getAngle` rotates CCW)
 *
 * @param {number[][]} path      — array of [lng, lat] points (≥ 2)
 * @param {number}     spacingM  — geographic spacing between arrows in metres
 * @returns {Array<{ position: [number, number], angle: number }>}
 */
export function sampleArrowPoints(path, spacingM) {
  if (!path || path.length < 2) return []

  const result = []
  let accumulated = spacingM / 2 // half-step in so first arrow isn't at the start point

  for (let i = 0; i < path.length - 1; i++) {
    const p0 = path[i]
    const p1 = path[i + 1]
    const segLen = distanceMetres(p0, p1)
    if (segLen === 0) continue

    while (accumulated <= segLen) {
      const t = accumulated / segLen
      result.push({
        position: [p0[0] + t * (p1[0] - p0[0]), p0[1] + t * (p1[1] - p0[1])],
        angle: -computeBearing(p0[0], p0[1], p1[0], p1[1]),
      })
      accumulated += spacingM
    }
    accumulated -= segLen
  }
  return result
}

/**
 * Convert a target screen-pixel arrow spacing into a geographic metres spacing
 * for the current zoom level. Lower bound only — an upper cap (was 500m) made
 * arrows tile densely on continent-scale views where natural spacing is in
 * the hundreds of kilometres.
 *
 * @param {number} targetPx — desired pixel gap between arrows (default 50, matches DOM `repeat: '50px'`)
 * @param {number} zoom     — Google Maps zoom level
 * @returns {number} spacing in metres
 */
export function arrowSpacingForZoom(targetPx = 50, zoom = 15) {
  const mPerPx = M_PER_PX_AT_EQUATOR / 2 ** zoom
  return Math.max(5, Math.round(targetPx * mPerPx))
}
